import type {
  ConfirmJobEquipmentInstallationsInput,
  PendingEquipmentInstallation,
} from '@/types/equipment';

export interface JobEquipmentInstallationUnitDraft {
  unitIndex: number;
  serialNumber: string;
  installDate: string;
  /** True when this position already has an Equipment record on the job. */
  persisted?: boolean;
  /** True after the operator edits a field during this form session. */
  dirty?: boolean;
}

export type JobEquipmentInstallationDrafts = Record<string, JobEquipmentInstallationUnitDraft[]>;

export interface EquipmentInstallationLineLike {
  _rowId?: string;
  snapshotId?: string;
  name?: string;
  type?: string;
  quantity?: number;
}

export type EquipmentInstallationValidation =
  | { ok: true; input: ConfirmJobEquipmentInstallationsInput | null }
  | { ok: false; message: string };

export function localDateString(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

export function installationLineId(line: EquipmentInstallationLineLike): string {
  return line.snapshotId?.trim() || line._rowId?.trim() || '';
}

/**
 * One draft row per physical unit. Quantity changes preserve the positions
 * that still exist and add only the new positions, so changing 2 -> 3 never
 * clears the first two serial numbers.
 */
export function resizeInstallationUnits(
  units: JobEquipmentInstallationUnitDraft[] | undefined,
  quantity: number,
  today = localDateString(),
): JobEquipmentInstallationUnitDraft[] {
  if (!Number.isInteger(quantity) || quantity < 1) return [];
  const byIndex = new Map((units ?? []).map((unit) => [unit.unitIndex, unit]));
  return Array.from({ length: quantity }, (_, offset) => {
    const unitIndex = offset + 1;
    return (
      byIndex.get(unitIndex) ?? {
        unitIndex,
        serialNumber: '',
        installDate: today,
      }
    );
  });
}

/** Hydrate the inline editor from the canonical job-equipment response. */
export function draftsFromInstallationState(
  lines: PendingEquipmentInstallation[],
  today = localDateString(),
): JobEquipmentInstallationDrafts {
  return Object.fromEntries(
    lines.map((line) => {
      const confirmed = new Map(line.confirmedUnits.map((unit) => [unit.unitIndex, unit]));
      return [
        line.lineItemSnapshotId,
        resizeInstallationUnits(undefined, line.quantity, today).map((unit) => {
          const saved = confirmed.get(unit.unitIndex);
          return saved
            ? {
                unitIndex: unit.unitIndex,
                serialNumber: saved.serialNumber ?? '',
                installDate: saved.installDate ?? today,
                persisted: true,
              }
            : unit;
        }),
      ];
    }),
  );
}

/**
 * Validate exactly the equipment lines the job save will persist, then shape
 * the existing installations API payload. The API remains the sole owner of
 * Equipment creation/update, warranty defaults, and line-unit idempotency.
 */
export function buildEquipmentInstallationsInput(
  lines: EquipmentInstallationLineLike[] | undefined,
  drafts: JobEquipmentInstallationDrafts,
): EquipmentInstallationValidation {
  const equipmentLines = (lines ?? []).filter(
    (line) => String(line.type ?? '').toUpperCase() === 'EQUIPMENT',
  );
  if (equipmentLines.length === 0) return { ok: true, input: null };

  const installations: ConfirmJobEquipmentInstallationsInput['installations'] = [];
  for (const line of equipmentLines) {
    const name = line.name?.trim() || 'Equipment';
    const quantity = Number(line.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      return {
        ok: false,
        message: `${name} must have a positive whole-number quantity before its units can be confirmed.`,
      };
    }
    const lineItemSnapshotId = installationLineId(line);
    if (!lineItemSnapshotId) {
      return { ok: false, message: `${name} is missing its line identifier.` };
    }

    const units = resizeInstallationUnits(drafts[lineItemSnapshotId], quantity);
    // Installation details are promised before completion, not before a job
    // can be created or scheduled. An untouched line therefore remains pending
    // and the existing completion guard will enforce it later. Once an operator
    // starts confirming a line, require every physical unit so no partial form
    // entry is silently discarded.
    const confirmationStarted = units.some(
      (unit) => unit.dirty || (!unit.persisted && Boolean(unit.serialNumber.trim())),
    );
    if (!confirmationStarted) continue;
    for (const unit of units) {
      if (!unit.serialNumber.trim()) {
        return {
          ok: false,
          message: `Enter a serial number for ${name}, unit ${unit.unitIndex}.`,
        };
      }
      if (!unit.installDate) {
        return {
          ok: false,
          message: `Confirm the installation date for ${name}, unit ${unit.unitIndex}.`,
        };
      }
    }
    installations.push({
      lineItemSnapshotId,
      units: units.map((unit) => ({
        unitIndex: unit.unitIndex,
        serialNumber: unit.serialNumber.trim(),
        installDate: unit.installDate,
      })),
    });
  }

  return {
    ok: true,
    input: installations.length > 0 ? { installations } : null,
  };
}
